/**
 * Predefined color themes with various color harmony combinations.
 * Based on color theory: triadic, split-complementary, analogous, etc.
 */

import type { Theme, ThemePalette } from "../types/theme";

// Helper to create HSL color string
const hsl = (h: number, s: number, l: number): string =>
  `hsl(${h}, ${s}%, ${l}%)`;
const hsla = (h: number, s: number, l: number, a: number): string =>
  `hsla(${h}, ${s}%, ${l}%, ${a})`;

// Normalize hue to 0-360 range
const normalizeHue = (h: number): number => ((h % 360) + 360) % 360;

/**
 * Options for palette generation - allows more variety in backgrounds
 */
interface PaletteOptions {
  bgSaturation?: number; // 0-100, default 20 for dark
  bgLightness?: number; // 0-100, default 10 for dark
  surfaceSaturation?: number; // 0-100
  surfaceLightness?: number; // 0-100
  neonMode?: boolean; // Extra bright, high saturation
}

/**
 * Generate a 7-color heptadic (complementary) palette from two base hues.
 * Creates a mathematically harmonious set of colors using analogous and split-complementary harmony.
 *
 * @param baseHue - Primary hue (0-360)
 * @param accentHue - Accent hue (0-360)
 * @param isDark - Whether to use dark or light mode
 * @param options - Additional options for customization
 * @returns ThemePalette with 7 core colors and derived semantic colors
 */
const generateHeptadicPalette = (
  baseHue: number,
  accentHue: number,
  isDark: boolean,
  options: PaletteOptions = {},
): ThemePalette => {
  // Normalize hues
  const base = normalizeHue(baseHue);
  const accent = normalizeHue(accentHue);

  // Extract options with defaults
  const bgSat = options.bgSaturation ?? (isDark ? 20 : 0);
  const bgLight = options.bgLightness ?? (isDark ? 10 : 98);
  const surfaceSat = options.surfaceSaturation ?? (isDark ? 15 : 0);
  const surfaceLight = options.surfaceLightness ?? (isDark ? 15 : 100);
  const neon = options.neonMode ?? false;

  // Lightness and saturation adjustments - light themes need darker colors for contrast
  const primarySat = neon ? 90 : isDark ? 70 : 75;
  const primaryLightness = neon ? 65 : isDark ? 55 : 40; // Darker for light themes
  const secondaryLightness = isDark ? 50 : 38; // Darker for light themes
  const tertiaryLightness = isDark ? 50 : 38;
  const accentLightness = neon ? 70 : isDark ? 60 : 45; // Darker for light themes
  const neutralLightness = isDark ? 40 : 50;

  // Generate 7 core complementary colors
  const primary = hsl(base, primarySat, primaryLightness);
  const secondary = hsl(normalizeHue(base + 30), 65, secondaryLightness); // Analogous +30°
  const tertiary = hsl(normalizeHue(base - 30), 65, tertiaryLightness); // Analogous -30°
  const accent1 = hsl(accent, neon ? 95 : 75, accentLightness);
  const accent2 = hsl(normalizeHue(accent + 45), 70, accentLightness - 2); // Split from accent
  const neutral = hsl(base, 10, neutralLightness); // Desaturated base
  const emphasis = hsl(
    normalizeHue(accent - 15),
    neon ? 100 : 85,
    accentLightness,
  ); // High saturation near accent

  // Background colors - now configurable!
  // For light themes: background is lightest, surface slightly darker for contrast
  const background = isDark
    ? hsl(base, bgSat, bgLight)
    : hsl(base, bgSat, bgLight);
  const surface = isDark
    ? hsl(base, surfaceSat, surfaceLight)
    : hsl(base, surfaceSat + 5, Math.max(surfaceLight - 4, 92)); // Slightly darker than bg
  const surfaceHover = isDark
    ? hsl(base, surfaceSat + 5, surfaceLight + 5)
    : hsl(base, 15, Math.max(surfaceLight - 8, 88)); // Darker on hover

  // Text colors - ensure high contrast for readability
  const textPrimary = isDark ? hsl(0, 0, 95) : hsl(base, 40, 10); // Very dark for light themes
  const textSecondary = isDark ? hsl(0, 0, 80) : hsl(base, 30, 25); // Dark for light themes
  const textMuted = isDark ? hsl(0, 0, 60) : hsl(base, 20, 45); // Medium-dark for light themes

  // Input colors (contrast adjusted for both modes)
  const inputBg = isDark ? hsl(base, bgSat, bgLight + 15) : hsl(0, 0, 100);
  const inputBorder = isDark ? hsl(base, 30, 40) : hsl(base, 40, 65); // More visible border for light
  const inputText = isDark ? hsl(0, 0, 95) : hsl(base, 40, 10); // Match textPrimary
  const inputPlaceholder = isDark ? hsl(0, 0, 55) : hsl(base, 20, 50); // Visible but muted

  // Status colors (derived from core colors)
  const success = isDark ? hsl(150, 60, 50) : hsl(150, 70, 40);
  const warning = isDark ? hsl(45, 90, 55) : hsl(45, 95, 50);
  const error = isDark ? hsl(0, 70, 55) : hsl(0, 75, 50);
  const info = isDark ? hsl(200, 70, 55) : hsl(200, 75, 50);

  // Card type colors (terrible psychiatrist specific)
  const cardQuestion = isDark ? hsl(45, 80, 50) : hsl(45, 85, 48);
  const cardAnswer = isDark ? hsl(190, 70, 50) : hsl(190, 80, 48);
  const cardTreatment = isDark ? hsl(330, 70, 60) : hsl(330, 70, 56);

  // Player status colors
  const statusThinking = isDark ? hsl(45, 80, 35) : hsl(45, 85, 80);
  const statusDone = isDark ? hsl(150, 60, 35) : hsl(150, 60, 80);
  const statusPsychologist = isDark ? hsl(base, 60, 40) : hsl(base, 50, 85);

  // Glass overlay
  const glassBackground = isDark
    ? hsla(base, bgSat + 10, surfaceLight, 0.85)
    : hsla(0, 0, 100, 0.92);
  const glassBorder = isDark
    ? hsla(accent, 50, 50, 0.3)
    : hsla(base, 30, 50, 0.25);

  return {
    primary,
    secondary,
    tertiary,
    accent: accent1, // For backward compatibility
    accent2,
    neutral,
    emphasis,
    background,
    surface,
    surfaceHover,
    textPrimary,
    textSecondary,
    textMuted,
    inputBackground: inputBg,
    inputBorder,
    inputText,
    inputPlaceholder,
    success,
    warning,
    error,
    info,
    cardQuestion,
    cardAnswer,
    cardTreatment,
    statusThinking,
    statusDone,
    statusPsychologist,
    glassBackground,
    glassBorder,
  };
};

// All predefined themes - varied backgrounds and styles
export const THEMES: Theme[] = [
  // === VIBRANT DARK THEMES WITH COLORED BACKGROUNDS ===
  {
    id: "cyberpunk-neon",
    name: "Cyberpunk Neon",
    description: "Hot pink and electric cyan - futuristic and bold",
    harmony: "complementary",
    isDark: true,
    palette: generateHeptadicPalette(320, 180, true, {
      bgSaturation: 60,
      bgLightness: 8,
      surfaceSaturation: 50,
      surfaceLightness: 12,
      neonMode: true,
    }),
  },
  {
    id: "deep-ocean",
    name: "Deep Ocean",
    description: "Rich navy with coral accents - mysterious depths",
    harmony: "complementary",
    isDark: true,
    palette: generateHeptadicPalette(220, 15, true, {
      bgSaturation: 70,
      bgLightness: 8,
      surfaceSaturation: 60,
      surfaceLightness: 12,
    }),
  },
  {
    id: "midnight-purple",
    name: "Midnight Purple",
    description: "Deep violet with gold highlights - royal and elegant",
    harmony: "complementary",
    isDark: true,
    palette: generateHeptadicPalette(270, 45, true, {
      bgSaturation: 50,
      bgLightness: 6,
      surfaceSaturation: 45,
      surfaceLightness: 10,
    }),
  },
  {
    id: "forest-night",
    name: "Forest Night",
    description: "Deep emerald with amber accents - natural mystery",
    harmony: "complementary",
    isDark: true,
    palette: generateHeptadicPalette(150, 35, true, {
      bgSaturation: 55,
      bgLightness: 6,
      surfaceSaturation: 45,
      surfaceLightness: 10,
    }),
  },
  {
    id: "blood-moon",
    name: "Blood Moon",
    description: "Deep crimson with teal accents - dramatic and intense",
    harmony: "complementary",
    isDark: true,
    palette: generateHeptadicPalette(355, 175, true, {
      bgSaturation: 60,
      bgLightness: 6,
      surfaceSaturation: 50,
      surfaceLightness: 10,
    }),
  },
  {
    id: "aurora-borealis",
    name: "Aurora Borealis",
    description: "Teal and magenta - dancing northern lights",
    harmony: "split-complementary",
    isDark: true,
    palette: generateHeptadicPalette(175, 300, true, {
      bgSaturation: 45,
      bgLightness: 8,
      surfaceSaturation: 40,
      surfaceLightness: 12,
      neonMode: true,
    }),
  },

  // === WARM/EARTHY THEMES ===
  {
    id: "sunset-blaze",
    name: "Sunset Blaze",
    description: "Warm orange fading to deep red - golden hour vibes",
    harmony: "analogous",
    isDark: true,
    palette: generateHeptadicPalette(25, 350, true, {
      bgSaturation: 50,
      bgLightness: 8,
      surfaceSaturation: 45,
      surfaceLightness: 12,
    }),
  },
  {
    id: "coffee-shop",
    name: "Coffee Shop",
    description: "Rich brown with cream accents - cozy and warm",
    harmony: "analogous",
    isDark: true,
    palette: generateHeptadicPalette(30, 45, true, {
      bgSaturation: 35,
      bgLightness: 10,
      surfaceSaturation: 30,
      surfaceLightness: 14,
    }),
  },

  // === COOL/CALM THEMES ===
  {
    id: "arctic-ice",
    name: "Arctic Ice",
    description: "Pale blue with white accents - crisp and clean",
    harmony: "analogous",
    isDark: true,
    palette: generateHeptadicPalette(195, 220, true, {
      bgSaturation: 30,
      bgLightness: 12,
      surfaceSaturation: 25,
      surfaceLightness: 16,
    }),
  },
  {
    id: "lavender-dreams",
    name: "Lavender Dreams",
    description: "Soft purple with pink hints - dreamy and peaceful",
    harmony: "analogous",
    isDark: true,
    palette: generateHeptadicPalette(280, 330, true, {
      bgSaturation: 35,
      bgLightness: 12,
      surfaceSaturation: 30,
      surfaceLightness: 16,
    }),
  },

  // === LIGHT THEMES ===
  {
    id: "light-modern",
    name: "Light Modern",
    description: "Clean white with blue accents - professional and fresh",
    harmony: "complementary",
    isDark: false,
    palette: generateHeptadicPalette(210, 30, false),
  },
  {
    id: "light-warm",
    name: "Light Warm",
    description: "Cream background with coral accents - friendly and inviting",
    harmony: "complementary",
    isDark: false,
    palette: generateHeptadicPalette(35, 195, false, {
      bgSaturation: 20,
      bgLightness: 96,
    }),
  },

  // === CLASSIC GREY-BASED (kept for accessibility) ===
  {
    id: "dark-classic",
    name: "Dark Classic",
    description: "Neutral grey with purple accents - timeless elegance",
    harmony: "triadic",
    isDark: true,
    palette: generateHeptadicPalette(270, 30, true),
  },
];

// Get theme by ID
export const getThemeById = (id: string): Theme | undefined => {
  return THEMES.find((theme) => theme.id === id);
};

// Get default theme
export const getDefaultTheme = (): Theme => {
  return THEMES[0]; // dark-triadic
};

// Apply theme to document (sets CSS variables)
export const applyTheme = (theme: Theme): void => {
  const root = document.documentElement;
  const { palette } = theme;

  // Core 7-color complementary system
  root.style.setProperty("--color-primary", palette.primary);
  root.style.setProperty("--color-secondary", palette.secondary);
  root.style.setProperty("--color-tertiary", palette.tertiary);
  root.style.setProperty("--color-accent", palette.accent); // accent1 (backward compatible)
  root.style.setProperty("--color-accent-1", palette.accent); // New semantic name
  root.style.setProperty("--color-accent-2", palette.accent2);
  root.style.setProperty("--color-neutral", palette.neutral);
  root.style.setProperty("--color-emphasis", palette.emphasis);

  // Background colors
  root.style.setProperty("--color-background", palette.background);
  root.style.setProperty("--color-surface", palette.surface);
  root.style.setProperty("--color-surface-hover", palette.surfaceHover);

  // Text colors
  root.style.setProperty("--color-text-primary", palette.textPrimary);
  root.style.setProperty("--color-text-secondary", palette.textSecondary);
  root.style.setProperty("--color-text-muted", palette.textMuted);

  // Input colors
  root.style.setProperty("--color-input-background", palette.inputBackground);
  root.style.setProperty("--color-input-border", palette.inputBorder);
  root.style.setProperty("--color-input-text", palette.inputText);
  root.style.setProperty("--color-input-placeholder", palette.inputPlaceholder);

  // Status colors
  root.style.setProperty("--color-success", palette.success);
  root.style.setProperty("--color-warning", palette.warning);
  root.style.setProperty("--color-error", palette.error);
  root.style.setProperty("--color-info", palette.info);

  // Card type colors
  root.style.setProperty("--color-card-question", palette.cardQuestion);
  root.style.setProperty("--color-card-answer", palette.cardAnswer);
  root.style.setProperty("--color-card-treatment", palette.cardTreatment);

  // Player status colors
  root.style.setProperty("--color-status-thinking", palette.statusThinking);
  root.style.setProperty("--color-status-done", palette.statusDone);
  root.style.setProperty(
    "--color-status-psychologist",
    palette.statusPsychologist,
  );

  // Glass overlay
  root.style.setProperty("--color-glass-background", palette.glassBackground);
  root.style.setProperty("--color-glass-border", palette.glassBorder);

  // Neo-brutalism border and shadow colors - adapt to theme
  // For dark themes, use black borders (high contrast)
  // For light themes, use a darker version of the background
  const borderColor = theme.isDark ? "#000000" : palette.textPrimary;
  const shadowColor = theme.isDark ? "#000000" : palette.textSecondary;

  // Update neo-brutalism border colors to use theme-aware colors
  root.style.setProperty("--neo-border-color", borderColor);
  root.style.setProperty("--neo-shadow-color", shadowColor);

  // Set dark/light mode class
  if (theme.isDark) {
    root.classList.add("dark");
    root.classList.remove("light");
  } else {
    root.classList.add("light");
    root.classList.remove("dark");
  }
};
